import { EventEmitter } from 'events';

interface ClientData {
    id: string;
    visits: any[];
    totalSpent: number;
    referrals: number;
    averageTip: number;
    firstVisit: Date;
    lastVisit: Date;
    noShowCount: number;
    rating: number;
}

interface BarberData {
    id: string;
    servicesCompleted: number;
    totalRevenue: number;
    averageServiceTime: number;
    clientSatisfaction: number;
    rebookingRate: number;
}

class AIManagerService extends EventEmitter {
    private static instance: AIManagerService;

    private constructor() {
        super();
    }

    static getInstance(): AIManagerService {
        if (!AIManagerService.instance) {
            AIManagerService.instance = new AIManagerService();
        }
        return AIManagerService.instance;
    }

    /**
     * Calculate Client Performance Index (CPI)
     * 30% Attendance + 25% Payment + 20% Consistency + 15% Referrals + 10% Tips
     */
    calculateCPI(client: ClientData): number {
        const visitScore = Math.min(100, client.visits.length * 10);
        const spendScore = Math.min(100, client.totalSpent / 100);
        const retentionScore = Math.min(100, this.daysSinceFirstVisit(client) / 30);
        const referralScore = Math.min(100, client.referrals * 20);
        const tipScore = Math.min(100, client.averageTip * 5);

        const cpi =
            (visitScore * 0.3) +
            (spendScore * 0.25) +
            (retentionScore * 0.2) +
            (referralScore * 0.15) +
            (tipScore * 0.1);

        return Math.round(Math.min(100, cpi));
    }

    /**
     * Calculate Barber Performance Index (BPI)
     * 25% Service Count + 25% Revenue + 20% Speed + 20% Satisfaction + 10% Rebooking
     */
    calculateBPI(barber: BarberData): number {
        const serviceScore = Math.min(100, barber.servicesCompleted / 10);
        const revenueScore = Math.min(100, barber.totalRevenue / 1000);
        const speedScore = Math.min(100, (30 / barber.averageServiceTime) * 100);
        const satisfactionScore = barber.clientSatisfaction * 20;
        const rebookingScore = barber.rebookingRate;

        const bpi =
            (serviceScore * 0.25) +
            (revenueScore * 0.25) +
            (speedScore * 0.2) +
            (satisfactionScore * 0.2) +
            (rebookingScore * 0.1);

        return Math.round(Math.min(100, bpi));
    }

    /**
     * Predict no-show probability
     */
    predictNoShow(client: ClientData): number {
        let probability = 0;

        // Base probability from history
        if (client.visits.length > 0) {
            probability = (client.noShowCount / client.visits.length) * 0.4;
        }

        // Days since last visit factor
        const daysSinceLastVisit = this.daysSince(client.lastVisit);
        if (daysSinceLastVisit > 60) probability += 0.2;
        if (daysSinceLastVisit > 90) probability += 0.2;

        // Low CPI factor
        const cpi = this.calculateCPI(client);
        if (cpi < 30) probability += 0.2;

        return Math.min(1, probability);
    }

    /**
     * Calculate optimal pricing
     */
    calculateOptimalPrice(
        basePrice: number,
        demand: number,
        timeOfDay: number,
        dayOfWeek: number
    ): number {
        let price = basePrice;

        // Demand-based pricing (±20%)
        if (demand > 0.8) price *= 1.2;
        else if (demand > 0.6) price *= 1.1;
        else if (demand < 0.3) price *= 0.9;

        // Time-based adjustments
        if (timeOfDay >= 17 && timeOfDay <= 19) price *= 1.15; // Peak hours
        if (timeOfDay >= 9 && timeOfDay <= 11) price *= 0.95; // Slow morning

        // Weekend premium
        if (dayOfWeek === 0 || dayOfWeek === 6) price *= 1.1;

        return Math.round(price);
    }

    /**
     * Detect potential fraud patterns
     */
    detectFraud(transaction: any): { isSuspicious: boolean; reasons: string[] } {
        const reasons: string[] = [];

        // Check for unusual tip amounts
        if (transaction.tipAmount && transaction.serviceAmount && transaction.tipAmount > transaction.serviceAmount * 0.5) {
            reasons.push('Unusually high tip (>50% of service)');
        }

        // Check for round number patterns
        if (transaction.amount && transaction.amount % 100 === 0 && transaction.amount > 100) {
            reasons.push('Suspicious round number amount');
        }

        return {
            isSuspicious: reasons.length > 0,
            reasons
        };
    }

    /**
     * Predict dynamic pricing based on demand, time, and day
     */
    predictDynamicPricing(serviceName: string, basePrice: number, datetime: Date): any {
        const hour = datetime.getHours();
        const day = datetime.getDay(); // 0 = Sunday, 6 = Saturday

        // Calculate demand factors
        const isPeakHour = (hour >= 17 && hour <= 19) || (hour >= 11 && hour <= 13);
        const isPeakDay = day === 5 || day === 6; // Friday or Saturday
        const isSlowMorning = hour >= 9 && hour <= 11;

        // Base multiplier
        let priceMultiplier = 1.0;
        const factors = {
            demandLevel: 'Normal Demand',
            timeOfDay: 'Standard Hours',
            isPeakDay: false
        };

        // Apply demand-based pricing
        if (isPeakHour && isPeakDay) {
            priceMultiplier = 1.25; // +25% for peak hour on peak day
            factors.demandLevel = 'Very High Demand';
            factors.timeOfDay = 'Peak Hours';
            factors.isPeakDay = true;
        } else if (isPeakHour) {
            priceMultiplier = 1.15; // +15% for peak hours
            factors.demandLevel = 'High Demand';
            factors.timeOfDay = 'Peak Hours';
        } else if (isPeakDay) {
            priceMultiplier = 1.10; // +10% for weekend
            factors.demandLevel = 'High Demand';
            factors.isPeakDay = true;
        } else if (isSlowMorning) {
            priceMultiplier = 0.95; // -5% discount for slow morning
            factors.demandLevel = 'Low Demand';
            factors.timeOfDay = 'Off-Peak Hours';
        }

        const suggestedPrice = Math.round(basePrice * priceMultiplier * 100) / 100;

        // Generate reasoning
        let reasoning = '';
        if (suggestedPrice > basePrice) {
            reasoning = `Suggest increasing price by ${Math.round((priceMultiplier - 1) * 100)}% due to ${factors.demandLevel.toLowerCase()} during ${factors.timeOfDay.toLowerCase()}.`;
        } else if (suggestedPrice < basePrice) {
            reasoning = `Suggest decreasing price by ${Math.round((1 - priceMultiplier) * 100)}% to attract customers during ${factors.timeOfDay.toLowerCase()}.`;
        } else {
            reasoning = 'Current pricing is optimal for this time period.';
        }

        return {
            suggestedPrice,
            basePrice,
            reasoning,
            factors,
            confidence: 0.85
        };
    }

    /**
     * Generate smart scheduling suggestions
     */
    generateSchedulingSuggestions(
        appointments: any[],
        barberPreferences: any,
        historicalData: any
    ): any[] {
        const suggestions: any[] = [];

        // Find gaps in schedule
        const gaps = this.findScheduleGaps(appointments);

        // Analyze historical peak times
        const peakTimes = this.analyzePeakTimes(historicalData);

        // Generate suggestions
        gaps.forEach(gap => {
            if (gap.duration >= 30) {
                const isPeakTime = peakTimes.some(peak =>
                    gap.start >= peak.start && gap.end <= peak.end
                );

                suggestions.push({
                    type: isPeakTime ? 'high-priority' : 'standard',
                    start: gap.start,
                    end: gap.end,
                    message: isPeakTime
                        ? 'High-demand time slot available'
                        : 'Open slot available',
                    recommendedService: this.recommendService(gap.duration)
                });
            }
        });

        return suggestions;
    }

    /**
     * Predict inventory needs
     */
    predictInventoryNeeds(
        currentInventory: any[],
        usageHistory: any[],
        upcomingAppointments: any[]
    ): any[] {
        const predictions: any[] = [];

        currentInventory.forEach(item => {
            const dailyUsage = this.calculateAverageDailyUsage(item.id, usageHistory);
            const daysUntilEmpty = item.quantity / dailyUsage;
            const expectedUsageNextWeek = dailyUsage * 7 * 1.2; // 20% buffer

            if (daysUntilEmpty < 7) {
                predictions.push({
                    itemId: item.id,
                    itemName: item.name,
                    currentQuantity: item.quantity,
                    daysUntilEmpty: Math.round(daysUntilEmpty),
                    recommendedOrderQuantity: Math.ceil(expectedUsageNextWeek * 2),
                    urgency: daysUntilEmpty < 3 ? 'critical' : 'high'
                });
            }
        });

        return predictions;
    }

    // Helper methods
    private daysSinceFirstVisit(client: ClientData): number {
        return this.daysSince(client.firstVisit);
    }

    private daysSince(date: Date): number {
        const now = new Date();
        const then = new Date(date);
        const diffTime = Math.abs(now.getTime() - then.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    private findScheduleGaps(appointments: any[]): any[] {
        const gaps = [];
        const sorted = appointments.sort((a, b) => a.start - b.start);

        for (let i = 0; i < sorted.length - 1; i++) {
            const gap = {
                start: sorted[i].end,
                end: sorted[i + 1].start,
                duration: sorted[i + 1].start - sorted[i].end
            };

            if (gap.duration > 0) {
                gaps.push(gap);
            }
        }

        return gaps;
    }

    private analyzePeakTimes(historicalData: any): any[] {
        // Simplified peak time analysis
        return [
            { start: 11, end: 14, day: 'weekday' }, // Lunch rush
            { start: 17, end: 19, day: 'weekday' }, // After work
            { start: 10, end: 16, day: 'weekend' }  // Weekend rush
        ];
    }

    private calculateAverageDailyUsage(itemId: string, history: any[]): number {
        const relevantHistory = history.filter(h => h.itemId === itemId);
        if (relevantHistory.length === 0) return 0;

        const totalUsage = relevantHistory.reduce((sum, h) => sum + h.quantity, 0);
        const days = this.daysSince(new Date(relevantHistory[0].date));

        return totalUsage / days;
    }

    private recommendService(duration: number): string {
        if (duration >= 60) return 'Full Service Package';
        if (duration >= 45) return 'Haircut & Beard';
        if (duration >= 30) return 'Quick Cut';
        return 'Touch-up';
    }
}

export const aiManager = AIManagerService.getInstance();
export default aiManager;